import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import { extractErrorMessage } from '../api';
import { useToast } from '../components/Toast';
import { useSales, useCreateSale } from '../hooks/useSales';
import { useProducts } from '../hooks/useProducts';

const Sales = () => {
  const { selectedStore } = useStore();
  const showToast = useToast();

  // Separate input state from the applied query params so fetches only trigger on "Apply"
  const [inputFilters, setInputFilters] = useState({ start_date: '', end_date: '', category_id: '' });
  const [appliedFilters, setAppliedFilters] = useState({});

  const salesQuery = useSales(appliedFilters);
  const productsQuery = useProducts();
  const createSale = useCreateSale();

  const sales = salesQuery.data ?? [];
  const products = productsQuery.data ?? [];
  const loading = salesQuery.isLoading;

  const [mutationError, setMutationError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '', quantity: 1, customer_name: '', customer_phone: '',
    payment_method: 'cash', discount_amount: 0,
  });

  // Helper functions
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: selectedStore?.currency || 'USD',
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getPaymentBadge = (method) => {
    const badges = {
      cash: 'badge-success',
      card: 'badge-primary',
      bank_transfer: 'badge-info',
      mobile_payment: 'badge-warning',
    };
    return badges[method] || 'badge-info';
  };

  const getPaymentLabel = (method) => {
    const labels = {
      cash: 'Cash',
      card: 'Card',
      bank_transfer: 'Bank Transfer',
      mobile_payment: 'Mobile Payment',
    };
    return labels[method] || method;
  };

  const renderIcon = (iconName) => {
    const icons = {
      dollar: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
      trendingUp: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
          <polyline points="17 6 23 6 23 12" />
        </svg>
      ),
      calendar: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
      shoppingBag: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
      ),
      receipt: (
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" />
          <path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1" />
          <path d="M2 21h20" />
          <path d="M7 8V3h10v5" />
          <path d="M10 3h4" />
        </svg>
      ),
    };
    return icons[iconName] || null;
  };

  // Calculate stats
  const stats = {
    total: sales.length,
    totalRevenue: sales.reduce((sum, sale) => sum + parseFloat(sale.total_price || 0), 0),
    todayRevenue: sales.filter(s => {
      const saleDate = new Date(s.sold_at);
      const today = new Date();
      return saleDate.toDateString() === today.toDateString();
    }).reduce((sum, sale) => sum + parseFloat(sale.total_price || 0), 0),
    weekRevenue: sales.filter(s => {
      const saleDate = new Date(s.sold_at);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return saleDate >= weekAgo;
    }).reduce((sum, sale) => sum + parseFloat(sale.total_price || 0), 0),
    avgOrder: sales.length > 0
      ? sales.reduce((sum, sale) => sum + parseFloat(sale.total_price || 0), 0) / sales.length
      : 0,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMutationError(null);
    try {
      await createSale.mutateAsync({ ...formData, store_id: selectedStore.id });
      showToast('Sale recorded successfully', 'success');
      resetForm();
    } catch (err) {
      setMutationError(extractErrorMessage(err, 'Failed to create sale'));
    }
  };

  const handleFilterApply = () => {
    const params = {};
    if (inputFilters.start_date) params.start_date = inputFilters.start_date;
    if (inputFilters.end_date) params.end_date = inputFilters.end_date;
    if (inputFilters.category_id) params.category_id = inputFilters.category_id;
    setAppliedFilters(params);
  };

  const resetForm = () => {
    setFormData({ product_id: '', quantity: 1, customer_name: '', customer_phone: '', payment_method: 'cash', discount_amount: 0 });
    setShowForm(false);
    setMutationError(null);
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div><span>Loading sales...</span></div>;
  }

  return (
    <div className="page-modern">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Sales</h1>
          <p className="page-subtitle">Track and manage all your sales transactions</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New Sale'}
        </button>
      </div>

      {mutationError && <div className="alert alert-danger">{mutationError}</div>}

      {/* Stats Row */}
      <div className="page-stats-row">
        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--primary)' }}>
            {renderIcon('shoppingBag')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Total Sales</div>
            <div className="page-stat-value">{formatNumber(stats.total)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--success)' }}>
            {renderIcon('dollar')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Today's Revenue</div>
            <div className="page-stat-value">{formatCurrency(stats.todayRevenue)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: 'var(--warning)' }}>
            {renderIcon('calendar')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">This Week</div>
            <div className="page-stat-value">{formatCurrency(stats.weekRevenue)}</div>
          </div>
        </div>

        <div className="page-stat-card">
          <div className="page-stat-icon" style={{ color: '#8B5CF6' }}>
            {renderIcon('trendingUp')}
          </div>
          <div className="page-stat-content">
            <div className="page-stat-label">Average Order</div>
            <div className="page-stat-value">{formatCurrency(stats.avgOrder)}</div>
          </div>
        </div>
      </div>

      {/* Filter Card */}
      <div className="card page-filter-card">
        <div className="page-filter-grid">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Start Date</label>
            <input
              type="date"
              className="form-input"
              value={inputFilters.start_date}
              onChange={(e) => setInputFilters({ ...inputFilters, start_date: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">End Date</label>
            <input
              type="date"
              className="form-input"
              value={inputFilters.end_date}
              onChange={(e) => setInputFilters({ ...inputFilters, end_date: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Category ID</label>
            <input
              type="text"
              className="form-input"
              placeholder="Filter by category..."
              value={inputFilters.category_id}
              onChange={(e) => setInputFilters({ ...inputFilters, category_id: e.target.value })}
            />
          </div>
          <button className="btn btn-secondary" onClick={handleFilterApply} style={{ alignSelf: 'flex-end' }}>
            Apply Filters
          </button>
        </div>
      </div>

      {/* Add Sale Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h3 className="card-title">Record New Sale</h3>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Product *</label>
                  <select
                    className="form-select"
                    value={formData.product_id}
                    onChange={(e) => setFormData({ ...formData, product_id: parseInt(e.target.value) })}
                    required
                  >
                    <option value="">Select Product</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name} - ${parseFloat(p.selling_price).toFixed(2)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Quantity *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.quantity}
                    min="1"
                    required
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.customer_name}
                    placeholder="Optional"
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Customer Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.customer_phone}
                    placeholder="Optional"
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <select
                    className="form-select"
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  >
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_payment">Mobile Payment</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Discount ($)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.discount_amount}
                    min="0"
                    step="0.01"
                    onChange={(e) => setFormData({ ...formData, discount_amount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={createSale.isPending}>
                  {createSale.isPending ? 'Processing...' : 'Complete Sale'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Data Table Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">All Sales ({sales.length})</h3>
          <div className="card-header-actions">
            <button className="btn btn-sm btn-secondary">Export</button>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {sales.length === 0 ? (
            <div className="page-empty-state">
              <div className="page-empty-icon">
                {renderIcon('receipt')}
              </div>
              <div className="page-empty-title">No sales recorded</div>
              <p className="page-empty-text">
                Record your first sale to start tracking revenue and performance
              </p>
              <button
                className="btn btn-primary"
                style={{ marginTop: '1.5rem' }}
                onClick={() => setShowForm(true)}
              >
                Record First Sale
              </button>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-container hidden-mobile">
                <table>
                  <thead>
                    <tr>
                      <th>Date & Time</th>
                      <th>Product</th>
                      <th style={{ textAlign: 'center' }}>Qty</th>
                      <th>Customer</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th>Payment Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map(sale => (
                      <tr key={sale.id}>
                        <td style={{ fontSize: '0.875rem' }}>{formatDate(sale.sold_at)}</td>
                        <td className="font-medium">{sale.product_name || `Product #${sale.product_id}`}</td>
                        <td style={{ textAlign: 'center' }}>{sale.quantity}</td>
                        <td>{sale.customer_name || '-'}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formatCurrency(sale.total_price)}
                        </td>
                        <td>
                          <span className={`badge ${getPaymentBadge(sale.payment_method)}`}>
                            {getPaymentLabel(sale.payment_method)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards hidden-desktop">
                {sales.map(sale => (
                  <div className="mobile-card" key={sale.id}>
                    <div className="mobile-card-header">
                      <div>
                        <div className="mobile-card-title">
                          {sale.product_name || `Product #${sale.product_id}`}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                          {sale.customer_name || 'Walk-in customer'}
                        </div>
                      </div>
                      <span className="font-bold" style={{ fontSize: '1.125rem' }}>
                        {formatCurrency(sale.total_price)}
                      </span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Quantity</span>
                      <span>{sale.quantity}</span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Payment</span>
                      <span className={`badge ${getPaymentBadge(sale.payment_method)}`}>
                        {getPaymentLabel(sale.payment_method)}
                      </span>
                    </div>
                    <div className="mobile-card-row">
                      <span className="mobile-card-label">Date</span>
                      <span>{formatDate(sale.sold_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sales;
